import { message } from "antd";
import { type InjectedFormikProps } from "formik";
import { Component, useReducer } from "react";

import { showAPIErrorMessage } from "utilities/apiHandler";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { LinkingAction } from "commonComponents/entity/accounting/AccountingLinkingOptions/AccountingLinkingOptions.api.types";
import { JobAccessContainer } from "commonComponents/entity/jobAccess/JobAccess/JobAccessContainer";
import PermissionWizard from "commonComponents/entity/permissionWizard/PermissionWizard/PermissionWizard";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";

import { FilterHandler, type IFilterHandler } from "entity/filters/Filter/Filter.api.handler";
import {
    type FilterEntity,
    FilterEntityType,
    type IFilterFormValues,
    mapFilterEntityToFormValues,
    type SelectedFilterItem,
} from "entity/filters/Filter/Filter.api.types";
import {
    defaultFilterState,
    type FilterActions,
    FilterReducer,
    type IFilterReducerState,
    SetUnsavedAppliedFilter,
    UpdateUnsavedChanges,
} from "entity/filters/Filter/Filter.reducer";
import { FilterDrawer } from "entity/filters/Filter/FilterDrawer";
import { type SavedFilterItem } from "entity/filters/SavedFilter/SavedFilter.api.types";
import { SubVendorHandler } from "entity/sub/SubVendor/SubVendor.api.handler";
import {
    type ISubVendorFormValues,
    SubPostSaveSteps,
} from "entity/sub/SubVendor/SubVendor.api.types";
import { type ISubVendorProps } from "entity/sub/SubVendor/SubVendorPresentational";

import "./JobAccessTab.less";

interface IJobAccessTabProps {
    filterHandler?: IFilterHandler;
    handler?: SubVendorHandler;
    disabled: boolean;
}

interface IUserJobAccessInternalProps extends IJobAccessTabProps {
    filterReducerState: IFilterReducerState;
    filterReducerDispatch: React.Dispatch<FilterActions>;
}

interface IJobAccessState {
    filterEntity?: FilterEntity;
    filterValues?: IFilterFormValues;
}

class JobAccess extends Component<
    InjectedFormikProps<ISubVendorProps & IUserJobAccessInternalProps, ISubVendorFormValues>
> {
    static defaultProps = {
        filterHandler: new FilterHandler(),
        handler: new SubVendorHandler(),
    };
    state: Readonly<IJobAccessState> = {
        filterEntity: undefined,
        filterValues: undefined,
    };

    componentDidMount = async () => {
        try {
            await this.loadJobs();
        } catch (e) {
            this.setState(() => {
                throw e;
            });
        }
    };

    componentDidUpdate = async (
        prevProps: InjectedFormikProps<
            ISubVendorProps & IUserJobAccessInternalProps,
            ISubVendorFormValues
        >,
    ) => {
        const { entity, values } = this.props;
        if (prevProps.entity.id !== entity.id) {
            await this.loadJobs();
        } else if (prevProps.values.jobAccess !== null && values.jobAccess === null) {
            await this.loadJobs(this.state.filterValues);
        }
    };

    private loadJobs = async (newFilterValues?: IFilterFormValues) => {
        const { filterHandler, entity, handler, setFieldValue } = this.props;
        let filters;

        if (!newFilterValues) {
            const filterResponse = await filterHandler!.get(FilterEntityType.SubDetailsJobsList);
            const filterValues = mapFilterEntityToFormValues(filterResponse);
            filters = filterValues;
            this.setState({ filterEntity: filterResponse });
        } else {
            filters = newFilterValues;
        }

        const response = await handler!.getJobAccess(entity.id, filters);
        setFieldValue("jobAccess", response);
    };

    private handleSaveFiltersUpdated = (newFilters: FilterEntity) => {
        this.setState({ filterEntity: newFilters });
    };

    private setUnsavedAppliedFilter = (values: SelectedFilterItem[], isChanged: boolean) =>
        this.props.filterReducerDispatch(new SetUnsavedAppliedFilter(values, isChanged));

    private handleFiltersUpdated = async (newFilters: IFilterFormValues) => {
        try {
            await this.loadJobs(newFilters);
            this.setState({ filterValues: newFilters });
            void message.success("Results updated");
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handlePermissionWizardComplete = async () => {
        const { onUpdatePostSaveSteps, values } = this.props;
        await onUpdatePostSaveSteps(
            values.createNewAccountingEntityOption === LinkingAction.Choose
                ? SubPostSaveSteps.LinkToAccounting
                : SubPostSaveSteps.None,
        );
    };

    private updateUnsavedChanges = (
        newFilters: SelectedFilterItem[],
        filterEntity: FilterEntity,
        savedFilter: SavedFilterItem,
    ) =>
        this.props.filterReducerDispatch(
            new UpdateUnsavedChanges(newFilters, filterEntity, savedFilter),
        );

    render() {
        const {
            values,
            setFieldValue,
            currentPostSaveStep,
            entity,
            jobIdsAdded,
            filterReducerState,
        } = this.props;
        const { filterEntity } = this.state;
        // Show loading spinner until entity is loaded
        if (!this.state || !this.state.filterEntity || !values.jobAccess) {
            return <BTLoading />;
        }
        return (
            <PageSection
                className="JobAccessTab"
                title={
                    <JobAccessTabHeader
                        filterButton={
                            filterEntity && (
                                <FilterDrawer
                                    filterId={FilterEntityType.SubDetailsJobsList}
                                    entity={filterEntity}
                                    onSubmit={this.handleFiltersUpdated}
                                    filterTypeEntity={FilterEntityType.SubDetailsJobsList}
                                    appliedFilter={filterReducerState.appliedFilter}
                                    setUnsavedAppliedFilter={this.setUnsavedAppliedFilter}
                                    showHasUnsavedChanges={filterReducerState.showHasUnsavedChanges}
                                    appliedFiltersFromSavedFilter={
                                        filterReducerState.appliedFiltersFromSavedFilter
                                    }
                                    onSavedFiltersUpdated={this.handleSaveFiltersUpdated}
                                    updateUnsavedChanges={this.updateUnsavedChanges}
                                />
                            )
                        }
                    />
                }
            >
                {currentPostSaveStep === SubPostSaveSteps.PermissionWizard &&
                    jobIdsAdded.length > 0 && (
                        <PermissionWizard
                            jobIds={jobIdsAdded}
                            onComplete={this.handlePermissionWizardComplete}
                            onCancel={this.handlePermissionWizardComplete}
                            subIds={[entity.id]}
                            modalConfig={{
                                parentRoute: "",
                                beforeClose: this.handlePermissionWizardComplete,
                            }}
                            useBds
                        />
                    )}
                <BTRow>
                    <BTCol flex="auto">
                        <JobAccessContainer
                            jobAccessEntity={values.jobAccess}
                            setFieldValue={setFieldValue}
                            disabled={!this.props.entity.canEditJobAccess || this.props.disabled}
                        />
                    </BTCol>
                </BTRow>
            </PageSection>
        );
    }
}

interface IJobAccessTabHeaderProps {
    filterButton?: React.ReactNode;
}

export const JobAccessTab: React.FC<
    InjectedFormikProps<ISubVendorProps & IJobAccessTabProps, ISubVendorFormValues>
> = (props) => {
    const [state, dispatch] = useReducer(FilterReducer, defaultFilterState);
    return <JobAccess {...props} filterReducerState={state} filterReducerDispatch={dispatch} />;
};

export const JobAccessTabHeader: React.FunctionComponent<IJobAccessTabHeaderProps> = (props) => {
    return (
        <div className="JobAccessTabHeader">
            <BTTitle level={2}>Job access</BTTitle>
            {props.filterButton}
        </div>
    );
};
